// Validation helpers for redirect steps.
//
// A redirect only causes a problem when a contact can arrive back at that same redirect
// without passing a delay or wait step. The api processes one step per queue pass, so such a
// loop would cycle the queue item forever. Redirects that only jump forwards are safe.

export function resetPathErrors(steps) {
    steps.forEach((step) => {
        if (Array.isArray(step)) {
            resetPathErrors(step)
        } else {
            step.pathError = false
        }
        if (step.steps) {
            resetPathErrors(step.steps)
        }
    })
}

// Builds the flow as a graph, mirroring findNextGuid in the api's automation job processor:
// a condition or split is left via the first step of one of its lanes, and a lane never falls
// through to whatever follows its parent.
export function buildStepGraph(steps) {
    const byGuid = new Map()
    const nextSteps = new Map()

    function walk(theSteps) {
        theSteps.forEach((step, index) => {
            if (Array.isArray(step)) {
                walk(step)
                return
            }
            // yes/no lane markers reuse their condition's guid, so they must not claim it
            if (step.guid && step.type !== 'yes' && step.type !== 'no' && !byGuid.has(step.guid)) {
                byGuid.set(step.guid, step)
            }
            if (step.steps && step.steps.length) {
                nextSteps.set(
                    step,
                    step.steps.map((lane) => lane[0]).filter((laneStep) => laneStep && !Array.isArray(laneStep))
                )
                step.steps.forEach((lane) => walk(lane))
            } else {
                const nextStep = theSteps[index + 1]
                if (nextStep && !Array.isArray(nextStep)) nextSteps.set(step, [nextStep])
            }
        })
    }
    walk(steps)

    return { byGuid, nextSteps }
}

// Walks forwards from startStep looking for the redirect it came from. Returns the steps that
// make up the loop so they can be marked for the user, or null when no loop is possible.
export function findRedirectLoop(steps, startStep, redirectGuid) {
    if (!startStep || !startStep.guid || !redirectGuid) return null

    const { byGuid, nextSteps } = buildStepGraph(steps)

    function getNextSteps(step) {
        if (step.type === 'delay' || step.type === 'wait') return []
        if (step.type === 'redirect') {
            const jumpGuid = step.stepOptions && step.stepOptions.redirectGuid && step.stepOptions.redirectGuid.value
            const jumpStep = jumpGuid ? byGuid.get(jumpGuid) : undefined
            return jumpStep ? [jumpStep] : []
        }
        return nextSteps.get(step) || []
    }

    const visited = new Set()
    const loopPath = []

    function walk(step) {
        if (visited.has(step)) return false
        visited.add(step)
        loopPath.push(step)
        if (step.type === 'redirect' && step.guid === redirectGuid) return true
        if (getNextSteps(step).some((nextStep) => walk(nextStep))) return true
        loopPath.pop()
        return false
    }

    return walk(startStep) ? loopPath : null
}
